import { coveredBy } from "@theme/vg-bundle-rules";

/**
 * <vg-swap-button data-add-variant="123" data-add-handle="everything-bundle" data-remove-variants="456,789" data-error="…">
 *   <button type="button">…</button>
 *   <p role="alert" data-vg-swap-status></p>
 * </vg-swap-button>
 *
 * Adds one unit of the target variant, then removes any cart lines for the listed variants. With
 * data-add-handle it also removes every cart line whose guides the target already delivers, so a swap
 * never leaves duplicate content. If the target is already in the cart, the add is skipped and only the
 * removals run. It goes through Shopify.actions.updateCart, so Horizon's cart drawer re-renders itself.
 * If the add succeeds but the removal step doesn't complete, it rolls the added quantity back to what it
 * was before the click, so the cart is left unchanged rather than holding both items. Once the swap
 * itself has completed, a later openCart() failure is only logged — the cart is already correct, so
 * nothing is rolled back or shown.
 *
 * Only one swap runs at a time across every button on the page. Inside <vg-cart-optimiser> the button
 * is rebuilt when the drawer re-renders mid-swap, so errors go to the optimiser, which keeps them.
 */

let swapInFlight = false;

/** @param {boolean} busy */
function setAllBusy(busy) {
  for (const button of document.querySelectorAll("vg-swap-button button")) {
    setBusy(/** @type {HTMLButtonElement} */ (button), busy);
  }
}

/**
 * @param {HTMLButtonElement} button
 * @param {boolean} busy
 */
function setBusy(button, busy) {
  button.disabled = busy;
  button.toggleAttribute("aria-busy", busy);
}

class VgSwapButton extends HTMLElement {
  connectedCallback() {
    this.addEventListener("click", this.#handleClick);
    const button = this.querySelector("button");
    if (swapInFlight && button) setBusy(button, true);
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.#handleClick);
  }

  /** @param {MouseEvent} event */
  #handleClick = async (event) => {
    const button = /** @type {HTMLElement} */ (event.target).closest("button");
    const actions = window.Shopify?.actions;
    const addVariant = this.dataset.addVariant;
    if (!button || button.disabled || swapInFlight || !actions || !addVariant)
      return;

    /** @type {(HTMLElement & { setSwapError?: (message: string) => void }) | null} */
    const optimiser = this.closest("vg-cart-optimiser");
    const status = this.querySelector("[data-vg-swap-status]");
    const addHandle = this.dataset.addHandle;
    const removeVariants = new Set(
      (this.dataset.removeVariants ?? "")
        .split(",")
        .filter(Boolean)
        .map(Number),
    );

    /** @param {string} message */
    const report = (message) => {
      if (optimiser?.setSwapError) optimiser.setSwapError(message);
      else if (status) status.textContent = message;
    };

    swapInFlight = true;
    setAllBusy(true);
    report("");

    const addVariantId = Number(addVariant);
    let addSucceeded = false;
    let swapped = false;
    let previousQuantity = 0;

    try {
      /** @type {{ items: { key: string, variant_id: number, handle: string, quantity: number }[] }} */
      const cartBefore = await fetch("/cart.js", {
        headers: { Accept: "application/json" },
      }).then((response) => response.json());
      previousQuantity =
        cartBefore.items.find((item) => item.variant_id === addVariantId)
          ?.quantity ?? 0;
      const removals = cartBefore.items
        .filter(
          (item) =>
            item.variant_id !== addVariantId &&
            (removeVariants.has(item.variant_id) ||
              (addHandle !== undefined && coveredBy(addHandle, item.handle))),
        )
        .map((item) => ({ id: item.key, quantity: 0 }));

      if (previousQuantity === 0) {
        const added = await actions.updateCart({
          lines: [{ merchandiseId: addVariant, quantity: 1 }],
        });
        if (added.userErrors?.length)
          throw new Error(added.userErrors[0]?.message);
        addSucceeded = true;
      }

      if (removals.length > 0) {
        const removed = await actions.updateCart({ lines: removals });
        if (removed.userErrors?.length)
          throw new Error(removed.userErrors[0]?.message);
      }
      swapped = true;

      await actions.openCart();
    } catch (error) {
      console.error("[vg-swap]", error);
      if (swapped) {
        // The swap itself (add + removal) already completed; openCart() merely failed to open the
        // drawer. The cart is correct, so there's nothing to roll back and nothing to tell the user.
      } else {
        if (addSucceeded) {
          try {
            /** @type {{ items: { key: string, variant_id: number }[] }} */
            const cartAfter = await fetch("/cart.js", {
              headers: { Accept: "application/json" },
            }).then((response) => response.json());
            const targetLine = cartAfter.items.find(
              (item) => item.variant_id === addVariantId,
            );
            if (targetLine) {
              await actions.updateCart({
                lines: [{ id: targetLine.key, quantity: previousQuantity }],
              });
            }
          } catch (rollbackError) {
            console.error("[vg-swap]", rollbackError);
          }
        }
        report(
          this.dataset.error || "Could not update your cart. Please try again.",
        );
      }
    } finally {
      swapInFlight = false;
      setAllBusy(false);
      setBusy(/** @type {HTMLButtonElement} */ (button), false);
    }
  };
}

if (!customElements.get("vg-swap-button"))
  customElements.define("vg-swap-button", VgSwapButton);
